import React from 'react';
import { Formik, Form } from 'formik';
import { useNavigate } from 'react-router-dom';
import { Button } from 'react-bootstrap';
import { Subject, ApiObject } from 'app/types';

import {
  useAddSubjectMutation,
  usePatchSubjectMutation,
  useGetBackofficeNewSubjectQuery,
  useGetBackofficeEditSubjectQuery,
  useDeleteSubjectAttachmentMutation,
} from 'services/exploreApi';
import Loader from 'common/components/Loader/Loader';
import styles from './SubjectForm.module.scss';
import { useParams } from 'react-router-dom';

const SubjectForm = (): React.ReactElement => {
  const navigate = useNavigate();
  const { id } = useParams();
  let subject: ApiObject<Subject> | undefined;
  let isSubjectLoading: boolean;
  if (id) {
    ({ data: subject, isLoading: isSubjectLoading } = useGetBackofficeEditSubjectQuery(id));
  } else {
    ({ data: subject, isLoading: isSubjectLoading } = useGetBackofficeNewSubjectQuery());
  }
  let Mutation: any;
  if (location.pathname.includes('new')) {
    [Mutation] = useAddSubjectMutation();
  } else {
    [Mutation] = usePatchSubjectMutation();
  }
  const [deleteAttachment] = useDeleteSubjectAttachmentMutation();
  const initialValues: Subject = { ...(subject?.data || ({} as Subject)) };
  if (isSubjectLoading) return <Loader />;
  return (
    <div className={styles.subjectFormContainer}>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        onSubmit={async (values) => {
          const formData = new FormData();
          formData.append(`subject[name]`, values.name);
          formData.append(`subject[description]`, values.description || '');
          formData.append(`subject[priority]`, String(values.priority) || '');
          formData.append(`subject[image]`, values.image || '');
          let result: any;
          if (id) {
            result = await Mutation({ data: formData, id: id });
          } else {
            result = await Mutation(formData);
          }
          if (result) {
            navigate(`/backoffice/subjects`);
          }
        }}
      >
        {({ values, handleChange, errors }) => (
          <Form className={styles.form}>
            <div className={`d-flex align-items-center gap-3 ${styles.header}`}>
              <h1>{id ? 'Edit Subject' : 'New Subject'}</h1>
            </div>
            <div className={styles.group}>
              <label htmlFor="name">Name</label>
              <input
                name="name"
                placeholder="Name"
                onChange={handleChange}
                defaultValue={values.name || ''}
              />
              {errors.name && <div className={styles.error}>{errors.name}</div>}
            </div>
            <div className={styles.group}>
              <label htmlFor="description">Description</label>
              <input
                name="description"
                placeholder="Description"
                onChange={handleChange}
                defaultValue={values.description || ''}
              />
              {errors.description && <div className={styles.error}>{errors.description}</div>}
            </div>
            <div className={styles.group}>
              <label htmlFor="priority">Priority</label>
              <input
                name="priority"
                placeholder="Priority"
                onChange={handleChange}
                value={values.priority || ''}
              />
            </div>
            <div className={styles.group}>
              <div className="d-flex align-items-center gap-2 mb-1">
                <label htmlFor="image">Image</label>
                {values.image && (
                  <i
                    className="bi bi-trash"
                    onClick={() => {
                      handleChange({
                        target: {
                          name: 'image',
                          value: '',
                        },
                      });
                      if (id) {
                        deleteAttachment(id);
                      }
                    }}
                  />
                )}
              </div>
              {values.image && !(values.image instanceof File) && (
                <div className="d-flex align-items-center gap-2">
                  {values.image.split('/')[values.image.split('/').length - 1]}
                  <i className="bi bi-download" onClick={() => window.open(values.image)} />
                </div>
              )}
              {values.image && (
                <img
                  src={
                    values.image instanceof File ? URL.createObjectURL(values.image) : values.image
                  }
                  className={`my-2 ${styles.image}`}
                />
              )}
              <input
                type="file"
                id="image"
                name="image"
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  const files = event.currentTarget.files;
                  if (files !== null) {
                    handleChange({
                      target: {
                        name: 'image',
                        value: files[0],
                      },
                    });
                  }
                }}
              />
            </div>
            <Button type="submit">Save Subject Form</Button>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default SubjectForm;
